import React, {
  useEffect, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Text from '../../../../shared/components/Text/Text';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { fetchCalendarActionHistory, undoCalendarAction } from '../../ducks/calendarActions';
import { SET_SELECTED_HISTORY_ENTRY_ID } from '../../ducks/actionTypes';
import useExpiryTick from '../../hooks/useExpiryTick';
import CALENDAR_ACTION_MERGE_WINDOW_MS from '../../ducks/calendarActionMergeWindow';

const DAY_LABELS = ['Su', 'M', 'Tu', 'W', 'Th', 'F', 'Sa'];

// Collapses a burst of rapid calendarAction changes (e.g. every item in a
// bulk operation) into one refetch instead of one per change.
const HISTORY_REFETCH_DEBOUNCE_MS = 400;

const HISTORY_INFO_TOOLTIP_ID = 'history-side-bar-tip';

const HISTORY_INFO_TOOLTIP_TEXT = [
  'These are the actions that have been performed in the last 30 minutes of programming.',
  'Actions in this list cannot be undone individually. They will need to be undone up to the selected item.',
  'Actions that already have been undone will have a strike through.',
  'Actions already undone cannot be restored.',
].join('\n\n');

const formatDate = (date) => (date ? moment(date).format('MMM D, YYYY') : '');

const formatDaysOfWeek = (days) => (days || [])
  .slice()
  .sort((a, b) => a - b)
  .map((d) => DAY_LABELS[d])
  .join('');

const getActorName = (user) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name || 'Someone';
};

// The name shown in a card's "Performed by" line - "You" for the signed-in
// coach's own actions, the actor's full name otherwise.
const getPerformerLabel = (entry, currentUser) => {
  const actorId = entry.actionDoneBy?.id;
  if (actorId && currentUser?.id && actorId === currentUser.id) return 'You';
  return getActorName(entry.actionDoneBy);
};

// Recurring items are stored once under this placeholder date (node-server's
// DATE_MEANS_RECURRING_ASSIGNMENT), so editing one occurrence records an
// assignment-update whose target date is the placeholder, not a real day.
const RECURRING_PLACEHOLDER_DATE = '0000-00-00';

// Exact values only, so a malformed real date still reads as a date problem
// rather than as a recurring edit. 'Invalid date' is how Sequelize can
// serialize the zero date, depending on driver settings.
const RECURRING_DATE_VALUES = new Set([null, undefined, '', RECURRING_PLACEHOLDER_DATE, 'Invalid date']);

const isRecurringPlaceholderDate = (date) => RECURRING_DATE_VALUES.has(date);

const firstAssignmentName = (entry) => entry.assignments?.[0]?.name || 'an exercise';
// otherItemsOverrideCount (set below for a card whose batchId is a KNOWN
// genuine multi-item gesture - see gestureParticipantCountByBatchId) takes
// priority over the assignments-array-length fallback, since the backend's
// /action-history response only ever returns one row per batch (confirmed:
// determinePreviousCalendarAction collapses every batch to a single root row
// before the frontend ever sees it) - entry.assignments realistically only
// ever has 1 item for assignment-update/swap, so without the override this
// would always read as "0 others" even for a genuine 4-item superset.
const otherAssignmentsCount = (entry) => (
  entry.otherItemsOverrideCount != null
    ? entry.otherItemsOverrideCount
    : Math.max((entry.assignments?.length || 0) - 1, 0)
);

// "and N other exercise(s)" - singular/plural on the noun itself.
const otherExercisesClause = (entry) => {
  const count = otherAssignmentsCount(entry);
  return count > 0 ? ` and ${count} other exercise${count === 1 ? '' : 's'}` : '';
};

// "and N other instance(s)" (update-future-occurrences).
const otherInstancesClause = (entry) => {
  const count = otherAssignmentsCount(entry);
  return count > 0 ? ` and ${count} other instance${count === 1 ? '' : 's'}` : '';
};

// "and N other(s)" (copy: individual assignments) - no noun to pluralize.
const othersClause = (entry) => {
  const count = otherAssignmentsCount(entry);
  return count > 0 ? ` and ${count} other${count === 1 ? '' : 's'}` : '';
};

/**
 * Mirrors node-server's CALENDAR_ACTION_NAME enum (models/constants.js).
 * The actor is deliberately NOT included here - it's already shown on the
 * card's "Performed by" subtext line below, so repeating it in the title
 * would be redundant.
 */
const ACTION_DESCRIPTION_BUILDERS = {
  'assignment-add': (entry) => `Added ${firstAssignmentName(entry)} to ${formatDate(entry.targetStartDate)}`,
  'assignment-update': (entry) => (isRecurringPlaceholderDate(entry.targetStartDate)
    ? `Updated recurring instance of ${firstAssignmentName(entry)}`
    : `Updated ${firstAssignmentName(entry)}${otherExercisesClause(entry)} on ${formatDate(entry.targetStartDate)}`),
  'assignment-delete': (entry) => `Deleted ${firstAssignmentName(entry)}${otherExercisesClause(entry)} on ${formatDate(entry.targetStartDate)}`,
  swap: (entry) => `Swapped ${firstAssignmentName(entry)} on ${formatDate(entry.targetStartDate)}`,
  reorder: (entry) => `Reordered ${firstAssignmentName(entry)}${otherExercisesClause(entry)} on ${formatDate(entry.targetStartDate)}`,
  'update-future-occurrences': (entry) => `Updated ${firstAssignmentName(entry)}${otherInstancesClause(entry)} starting on ${formatDate(entry.targetStartDate)}`,
  'move-date': (entry) => `Moved ${firstAssignmentName(entry)}${otherExercisesClause(entry)} from ${formatDate(entry.sourceStartDate)} to ${formatDate(entry.targetStartDate)}`,
  clear: (entry) => {
    const start = formatDate(entry.targetStartDate);
    const end = formatDate(entry.targetEndDate);
    // A single-day clear has nothing to range - "(from X to X)" just repeats the date.
    const rangeClause = start !== end ? ` (from ${start} to ${end})` : '';
    return `Cleared ${start}${rangeClause} of ${entry.assignments?.length || 0} exercises`;
  },
  load: (entry) => `Loaded ${entry.program?.name || 'a program'} starting on ${formatDate(entry.targetStartDate)}, ending on ${formatDate(entry.targetEndDate)}`,
  'make-recurring': (entry) => `Converted ${firstAssignmentName(entry)} to a recurring assignment starting on ${formatDate(entry.targetStartDate)}`,
  'update-recurring': (entry) => `Updated the recurring schedule for ${firstAssignmentName(entry)} on ${formatDate(entry.targetStartDate)}`,
  'delete-recurring': (entry) => `Deleted the ${firstAssignmentName(entry)} recurring assignment starting on ${formatDate(entry.targetStartDate)}`,
  'individual-assignments': (entry) => `Copied ${firstAssignmentName(entry)}${othersClause(entry)} to ${formatDate(entry.targetStartDate)}`,
  'copy-single': (entry) => `Copied all assignments from ${entry.sourceCalendar?.name || 'a calendar'} on ${formatDate(entry.sourceStartDate)} to ${formatDate(entry.targetStartDate)}`,
  'copy-multiple': (entry) => `Copied all assignments from ${entry.sourceCalendar?.name || 'a calendar'} on ${formatDate(entry.sourceStartDate)} to a range from ${formatDate(entry.targetStartDate)} to ${formatDate(entry.targetEndDate)} on ${formatDaysOfWeek(entry.targetDaysOfWeek)}`,
  'copy-range': (entry) => `Copied all assignments from ${entry.sourceCalendar?.name || 'a calendar'} between ${formatDate(entry.sourceStartDate)} and ${formatDate(entry.sourceEndDate)} to ${formatDate(entry.targetStartDate)} and ${formatDate(entry.targetEndDate)}`,
};

const describeEntry = (entry) => {
  const builder = ACTION_DESCRIPTION_BUILDERS[entry.actionType];
  return builder ? builder(entry) : `Made a change on ${formatDate(entry.targetStartDate)}`;
};

// Undoing an older entry cascades server-side (it also reverts any newer,
// still-undoable entries on the same calendar/athlete) - canBeUndone alone
// doesn't capture the 30-minute TTL, so both are checked here.
const isEntryUndoable = (entry) => Boolean(entry.canBeUndone)
  && (!entry.expiresAt || moment(entry.expiresAt).isAfter(moment()));

// Bulk gestures (drag-to-combine supersetting, multi-select edits) fire N
// independent assignment-update/swap requests for what the coach experiences
// as ONE action - each gets its own row/id server-side by design (see
// persistSupersetGrouping's batchId comment: sharing a batchId across those
// rows would silently drop every item after the first). This groups that
// burst for DISPLAY only.
//
// Only assignment-update and swap are ever produced by a genuine per-item
// loop for one coach gesture (persistSupersetGrouping's loop, and
// drag-combine, both call only updateProgrammedCalendarItem/
// swapProgrammedItemOnChildCalendar/updateSavedWorkoutItem per item - see the
// no-await-in-loop markers in persistSupersetGrouping). Every other action
// type either has a bulk-capable single endpoint that already folds a
// genuine multi-item gesture into ONE history row (delete, move-date both
// take an assignedIds array), or is inherently one-item-per-gesture with no
// UI flow that batches multiple different items into one action (add,
// convert/delete/update-schedule recurring, update-future-occurrences).
const GESTURE_MERGEABLE_ACTION_TYPES = new Set(['assignment-update', 'swap']);

// "Same gesture" requires matching batchId, not just same-day/actor/timing -
// a genuine superset shares ONE batchId across every participant's request
// (getCombineSharedBatchId.js, persistSupersetGrouping's sharedBulkBatchId);
// a plain sequential edit gets its own. targetStartDate/actor/timing alone
// can't tell those apart - two separate, unrelated edits landing close
// together on the same day satisfy that heuristic just as well as a real
// superset does. batchIdByCalendarActionId is a LIVE, session-observed map
// (calendarDataReducer.js's buildCalendarActionSetPatch, built from the same
// batchId every action creator already sends) - the /action-history response
// itself doesn't carry batchId, so this is the precise signal in place of it.
// Entries this session hasn't observed a batchId for (e.g. history loaded
// fresh before any live edit) never merge - conservative by design, matching
// "only supersets should ever be lumped."
// Defaults defensively to {} - state.calendar.data.batchIdByCalendarActionId
// should always be an object per initialState, but this runs on every render
// of a panel that's easy to hit mid-navigation/HMR, so a momentarily-missing
// map must degrade to "nothing known yet" (never merge) rather than crash.
const groupHistoryEntries = (history, batchIdByCalendarActionId = {}) => {
  const ascending = [...history].sort((a, b) => a.id - b.id);
  const groups = [];
  ascending.forEach((entry) => {
    const currentGroup = groups[groups.length - 1];
    const prior = currentGroup?.members[currentGroup.members.length - 1];
    const entryBatchId = batchIdByCalendarActionId?.[entry.id];
    const isSameGesture = prior
      && GESTURE_MERGEABLE_ACTION_TYPES.has(entry.actionType)
      && entry.actionType === prior.actionType
      && entry.targetStartDate === prior.targetStartDate
      && entry.actionDoneBy?.id === prior.actionDoneBy?.id
      && Math.abs(moment(entry.createdAt).valueOf() - moment(prior.createdAt).valueOf())
        < CALENDAR_ACTION_MERGE_WINDOW_MS
      && entryBatchId != null
      && entryBatchId === batchIdByCalendarActionId?.[prior.id];
    if (isSameGesture) {
      currentGroup.members.push(entry);
    } else {
      groups.push({ members: [entry] });
    }
  });
  // Newest group first (matches the list's newest-on-top order); the
  // lowest-id member (members[0], since built ascending) is the group's
  // "target" - the id the backend's undo cascade actually sweeps from,
  // mirroring the same LOWEST-id reasoning the reducer's own merge logic uses.
  return groups.reverse().map((group) => ({ ...group, targetEntry: group.members[0] }));
};

// Combines a group's members for describeEntry - the target carries the
// group's identity (id/actionType/date/actor), but assignments needs every
// member's so "and N others" counts the whole group, not just the target's
// own (typically single-item) assignments array.
const buildGroupDisplayEntry = (group) => ({
  ...group.targetEntry,
  assignments: group.members.flatMap((member) => member.assignments || []),
});

const Wrapper = styled('div')`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 16px;
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
`;

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoIcon = styled('span')`
  display: inline-flex;
  cursor: help;
`;

const ScrollBody = styled('div')`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 8px;
  overflow: auto;
`;

// No border - selection is shown as a background fill instead. The fill
// (surfaceSelectedDestructive) is the warmup-exercise red at low opacity, so
// the card tints red without hurting the text's legibility in either mode.
const EntryRow = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 8px;
  border-radius: 4px;
  background: ${({ isHighlighted, theme }) => (isHighlighted ? theme.colors.surfaceSelectedDestructive : 'transparent')};
  opacity: ${(props) => (props.isGreyedOut ? 0.5 : 1)};
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  transition: background-color 150ms ease;
`;

const EntryText = styled(Text)`
  text-decoration: ${(props) => (props.isGreyedOut ? 'line-through' : 'none')};
`;

const CenteredState = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

// A quiet note appended as the last row of the list (only when the list has
// entries) explaining the undo window - not a persistent footer, since an
// empty list already has its own "No recent changes" message covering that
// space.
const ListFooterText = styled('div')`
  padding: 12px 8px 54px;
  text-align: center;
`;

// Wraps the undo button so the warning can hover directly above it (absolute,
// out of flow) instead of pushing the button down when a selection is made.
const UndoFooter = styled('div')`
  position: relative;
`;

// Matches the "About Item" view-only banner's styling (ViewOnlyWarning in
// CreateNewComponentModal.js) - the app's existing alert-banner treatment -
// so this reads as the same kind of warning, just floating above the button
// instead of sitting inline.
const UndoWarningText = styled('div')`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.warningSurface};
  border: 2px solid ${({ theme }) => theme.colors.warningBorder};
  border-radius: 8px;
  font-family: 'Nunito Sans', sans-serif;
  letter-spacing: 0.3px;
  line-height: 1.4;
`;

// Mirrors the "Create New Exercise" CTA at the bottom of the exercises sidebar
// (ExerciseDataBaseButton) so the sidebars' bottom buttons match: same
// buttonPanelBg fill, 6px/16px padding-driven height, 16px/700 text, 8px gap,
// and hover fade.
const UndoCTAButton = styled('button')`
  width: 100%;
  padding: 6px 16px;
  background: ${({ theme }) => theme.colors.buttonPanelBg};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  transition: all 250ms ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  :hover {
    opacity: 0.7;
  }
  :disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const HistorySideBarContent = () => {
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const { buildUndo } = useFlags();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  // A plain timestamp the reducer refreshes on every relevant dispatch - see
  // the refetch effect below for why this drives it instead of
  // calendarAction itself.
  const calendarActionSetAt = useSelector((state) => state.calendar.data.calendarActionSetAt);
  // Live, session-observed id -> batchId map - see groupHistoryEntries above
  // for why this (not the /action-history response, which carries no
  // batchId) is what lets a genuine superset be told apart from two separate
  // edits landing close together.
  const batchIdByCalendarActionId = useSelector(
    (state) => state.calendar.data.batchIdByCalendarActionId,
  ) || {};
  // Participant count for a KNOWN genuine multi-item gesture's batchId,
  // recorded client-side the moment the gesture was initiated (drag-combine,
  // SuperSetPickerPopup's multi-select, persistSupersetGrouping's
  // bulk-select) - see calendarDataReducer.js for why this is the only safe
  // source for "and N other items" text, and why it's guarded against
  // ordinary single-item edit sessions.
  const gestureParticipantCountByBatchId = useSelector(
    (state) => state.calendar.data.gestureParticipantCountByBatchId,
  ) || {};
  const calendarActionHistory = useSelector(
    (state) => state.calendar.data.calendarActionHistory,
  );
  const isFetchingCalendarActionHistory = useSelector(
    (state) => state.calendar.data.isFetchingCalendarActionHistory,
  );
  const calendarActionHistoryError = useSelector(
    (state) => state.calendar.data.calendarActionHistoryError,
  );
  const isUndoingCalendarAction = useSelector(
    (state) => state.calendar.data.isUndoingCalendarAction,
  );

  // Re-renders exactly when an entry's expiresAt passes (and reschedules for
  // the next-soonest), so the greyed/"Expired" state below flips live instead
  // of waiting on some unrelated re-render. Already-undone entries don't need
  // watching - their state can't change again.
  useExpiryTick(
    calendarActionHistory.filter((entry) => !entry.undoneAt).map((entry) => entry.expiresAt),
  );

  // The card the coach clicked - undoing it cascades server-side to every
  // still-undoable entry newer than it, so this is the sole "boundary" of
  // what's about to be undone, not a multi-select of individually-picked rows.
  //
  // Lives in Redux (not component state) specifically so it can be cleared
  // directly and unconditionally the instant ANY undo succeeds - see
  // UNDO_CALENDAR_ACTION_FULFILLED in calendarDataReducer.js. The header undo
  // button and Cmd/Ctrl+Z both undo independently of this component entirely
  // (they target state.calendar.data.calendarAction, not this selection), so
  // component state alone could never react to them directly; the reducer
  // clearing this field itself, in the same action, is what makes "click
  // Undo -> the sidebar highlight is gone" unconditional and immediate rather
  // than something inferred later from a history refetch.
  const selectedEntryId = useSelector((state) => state.calendar.data.selectedHistoryEntryId);
  const setSelectedEntryId = (id) => dispatch({ type: SET_SELECTED_HISTORY_ENTRY_ID, payload: id });
  const selectedEntry = calendarActionHistory.find((entry) => entry.id === selectedEntryId) || null;

  // A live expiry tick can ALSO disqualify the current selection (the TTL
  // window closing while it just sits there, unrelated to any undo click) -
  // that's not covered by the reducer clearing above, so it's still handled
  // here reactively.
  useEffect(() => {
    if (selectedEntry && (selectedEntry.undoneAt || !isEntryUndoable(selectedEntry))) {
      setSelectedEntryId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntry]);

  // Groups a burst of same-gesture entries (e.g. every row a bulk superset
  // save produces) into one display card - see groupHistoryEntries above.
  // This also fixes the same-second createdAt ordering the old sort-only
  // approach handled: groups are built and ordered by id, not createdAt.
  const groupedHistory = groupHistoryEntries(calendarActionHistory, batchIdByCalendarActionId);

  // Refetches whenever: this panel mounts, the selected calendar/athlete
  // changes (previously left the prior calendar's history showing), or
  // calendarActionSetAt changes - which every calendar mutation (add/edit/
  // delete/swap/reorder/undo/etc.) updates, so the list stays live while the
  // coach keeps working with the panel open, not just on open/close.
  // Debounced: a single bulk operation dispatches its own FULFILLED action
  // once per item, so without this the panel would flash to its loading
  // spinner and back once per item in a tight burst instead of refetching
  // once after things settle.
  //
  // This deliberately watches calendarActionSetAt, NOT calendarAction -
  // calendarAction's own reference is intentionally held stable by the
  // reducer's merge-window logic (buildCalendarActionSetPatch) across a
  // single bulk gesture's several FULFILLED dispatches, so the header's
  // quick-undo button keeps targeting the whole burst with one click. That
  // same stability is wrong here: a second bulk edit made quickly enough to
  // land inside that merge window (e.g. re-supersetting the same 4 items
  // again right after the first change) keeps pointing at the FIRST
  // gesture's calendarAction object - same reference, no re-render, no
  // refetch - even though real new history exists. calendarActionSetAt is a
  // plain timestamp the reducer refreshes on every relevant dispatch
  // regardless of whether calendarAction's reference actually changed, so it
  // never misses a beat the way calendarAction alone can.
  const refetchTimeoutRef = useRef(null);
  useEffect(() => {
    if (!currentUser?.accountCode) return undefined;
    clearTimeout(refetchTimeoutRef.current);
    refetchTimeoutRef.current = setTimeout(() => {
      dispatch(fetchCalendarActionHistory(currentUser.accountCode));
    }, HISTORY_REFETCH_DEBOUNCE_MS);
    return () => clearTimeout(refetchTimeoutRef.current);
  }, [
    currentUser?.accountCode,
    selectedReduxCalendar?.id,
    selectedReduxAthlete?.id,
    calendarActionSetAt,
    dispatch,
  ]);

  // A card lights up when it's the selected boundary or newer than it (and
  // still eligible) - i.e. it's actually part of what undoing the selection
  // would cascade through. An already-undone or expired entry never lights
  // up even if it's positioned above the selection, since undoing the
  // selection can't touch it either way. Compared by id, not createdAt:
  // createdAt has whole-second precision, so rapid sequential requests (e.g. a
  // bulk superset) can land in the same second - mirroring the backend's own
  // cascade query (id: {[Op.gt]: targetActionId}).
  const isCardHighlighted = (entry) => {
    // The selected entry itself must still be a valid boundary - otherwise
    // (e.g. the header undo button or Cmd/Ctrl+Z reverted it independently of
    // this selection, or it expired) nothing should highlight, including any
    // entry newer than it that would otherwise read as "part of the cascade."
    if (!selectedEntry || !isEntryUndoable(selectedEntry)) return false;
    if (entry.id === selectedEntry.id) return true;
    return !entry.undoneAt
      && isEntryUndoable(entry)
      && entry.id > selectedEntry.id;
  };

  // The history CARDS that undoing the current selection will revert - each
  // grouped card is one logical action, so this is the "N" in "Undo N Actions".
  // A card counts if any of its members is highlighted (matching the row
  // highlighting the coach sees). Zero when nothing is selected.
  const undoneCardCount = groupedHistory.filter(
    (group) => group.members.some((member) => isCardHighlighted(member)),
  ).length;

  // A pending undo requires a selected card that's still eligible (a live
  // expiry can disqualify one after selection). Drives the button's enabled
  // state, its label, and whether the "cannot be restored" warning shows.
  const canUndo = Boolean(selectedEntry) && isEntryUndoable(selectedEntry);

  const handleCardClick = (entry) => (e) => {
    e.stopPropagation();
    if (entry.undoneAt || !isEntryUndoable(entry)) return;
    setSelectedEntryId(selectedEntryId === entry.id ? null : entry.id);
  };

  const handleDeselectAll = () => setSelectedEntryId(null);

  const handleConfirmUndo = () => {
    if (!selectedEntry || isUndoingCalendarAction || !currentUser?.accountCode) return;
    dispatch(undoCalendarAction(currentUser.accountCode, {
      id: selectedEntry.id,
      expiresAt: selectedEntry.expiresAt,
    }));
    // Optimistic - UNDO_CALENDAR_ACTION_FULFILLED will also clear this
    // unconditionally once the request resolves, but clearing it here too
    // gives instant feedback on this button's own click rather than waiting
    // on the network round-trip.
    setSelectedEntryId(null);
  };

  return (
    <Wrapper>
      <TitleRow>
        <Text fontWeight={700} fontSize='13px'>Action History</Text>
        <InfoIcon
          data-tooltip-id={HISTORY_INFO_TOOLTIP_ID}
          data-tooltip-content={HISTORY_INFO_TOOLTIP_TEXT}
        >
          <IcomoonReact iconSet={iconSet} size={14} icon='info2' color={colors.textSecondary} />
        </InfoIcon>
      </TitleRow>
      {isFetchingCalendarActionHistory && (
        <CenteredState><Spinner darkTheme /></CenteredState>
      )}
      {!isFetchingCalendarActionHistory && calendarActionHistoryError && (
        <CenteredState>
          <Text fontSize='13px' color={colors.textMuted}>Couldn&apos;t load recent changes.</Text>
        </CenteredState>
      )}
      {!isFetchingCalendarActionHistory && !calendarActionHistoryError && (
        <ScrollBody onClick={handleDeselectAll}>
          {!groupedHistory.length && (
            <CenteredState>
              <Text fontSize='13px' color={colors.textMuted}>No recent changes.</Text>
            </CenteredState>
          )}
          {groupedHistory.map((group) => {
            const { targetEntry } = group;
            // "Fully" rather than "any" - a group is only server-confirmed
            // undone once every member is, since the cascade normally sweeps
            // the whole group together (a partial state can only happen from
            // an unrelated interleaved undo - see isCardHighlighted below).
            const isFullyUndone = group.members.every((member) => Boolean(member.undoneAt));
            const isExpired = !isFullyUndone && !isEntryUndoable(targetEntry);
            const isGreyedOut = isFullyUndone || isExpired;
            const clickable = !isGreyedOut;
            const isHighlighted = group.members.some((member) => isCardHighlighted(member));
            // Not-yet-undone cards read in the primary text colour; once
            // undone/expired they fade to secondary (and the row's
            // line-through/opacity reinforces the state).
            const textColor = isGreyedOut ? colors.textSecondary : colors.text;
            // "and N other exercises" for a genuine superset - only ever
            // resolves for assignment-update/swap cards whose batchId was
            // recorded as a real multi-item gesture (see
            // gestureParticipantCountByBatchId above); every other card falls
            // through to buildGroupDisplayEntry's existing single-item count.
            // The explicit type check is a deliberate second guardrail, not
            // just relying on the fact that only update/swap gestures ever
            // populate gestureParticipantCountByBatchId in practice.
            const targetBatchId = GESTURE_MERGEABLE_ACTION_TYPES.has(targetEntry.actionType)
              ? batchIdByCalendarActionId[targetEntry.id]
              : undefined;
            const gestureParticipantCount = targetBatchId
              ? gestureParticipantCountByBatchId[targetBatchId]
              : undefined;
            const displayEntry = {
              ...buildGroupDisplayEntry(group),
              ...(gestureParticipantCount > 1
                ? { otherItemsOverrideCount: gestureParticipantCount - 1 }
                : {}),
            };
            return (
              <EntryRow
                key={targetEntry.id}
                isGreyedOut={isGreyedOut}
                isHighlighted={isHighlighted}
                clickable={clickable}
                onClick={clickable ? handleCardClick(targetEntry) : undefined}
              >
                <EntryText
                  fontSize='16px'
                  fontWeight={isHighlighted ? 700 : 400}
                  color={textColor}
                  isGreyedOut={isGreyedOut}
                >
                  {describeEntry(displayEntry)}
                </EntryText>
                {/* createdAt arrives in UTC; moment() here renders in the browser's
                    local time zone. Absolute, not relative ("N minutes ago") - a
                    relative label can read as wrong if the list itself goes stale.
                    Seconds are shown so same-second bursts stay distinguishable. */}
                <EntryText fontSize='11px' color={textColor} isGreyedOut={isGreyedOut}>
                  {`Performed by ${getPerformerLabel(targetEntry, currentUser)} | `}
                  {moment(targetEntry.createdAt).format('MMM D, YYYY [@] h:mm:ss a')}
                </EntryText>
              </EntryRow>
            );
          })}
          {/* Appended as the list's last row, only once there's actually a
              list to append to - an empty list already shows its own "No
              recent changes" message, so this note has nothing to sit under. */}
          {Boolean(groupedHistory.length) && (
            <ListFooterText>
              <Text fontSize='11px' color={colors.text}>Actions time out after 30 minutes</Text>
            </ListFooterText>
          )}
        </ScrollBody>
      )}
      {/* Button is always shown: greyed/disabled "Undo" with nothing selected,
          and an active "Undo N Actions" once a card is selected. The warning
          hovers above it only while an undo is actually pending. */}
      {buildUndo && (
        <UndoFooter>
          {canUndo && (
            <UndoWarningText>
              <IcomoonReact iconSet={iconSet} size={18} icon='warning' color='#F59E0B' />
              <Text fontSize='13px' fontWeight={700} color={colors.warningText}>
                Once actions are undone they cannot be restored.
              </Text>
            </UndoWarningText>
          )}
          <UndoCTAButton
            type='button'
            onClick={handleConfirmUndo}
            disabled={!canUndo || isUndoingCalendarAction}
          >
            {isUndoingCalendarAction && <Spinner />}
            {!isUndoingCalendarAction && (canUndo
              ? `Undo ${undoneCardCount} Action${undoneCardCount === 1 ? '' : 's'}`
              : 'Undo')}
          </UndoCTAButton>
        </UndoFooter>
      )}
      {/* Portaled to document.body so it escapes the sidebar's stacking context,
          matching the pattern used for the other calendar sidebar tooltips. */}
      {typeof document !== 'undefined' && createPortal(
        <ReactTooltip
          id={HISTORY_INFO_TOOLTIP_ID}
          variant='light'
          place='right'
          opacity={1}
          border={`1px solid ${colors.borderMedium}`}
          style={{
            backgroundColor: colors.tooltipSurface,
            color: colors.text,
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
            fontWeight: 400,
            borderRadius: '4px',
            padding: '8px',
            boxShadow: colors.shadowPanel,
            maxWidth: '260px',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            zIndex: 9999,
          }}
        />,
        document.body,
      )}
    </Wrapper>
  );
};

export default HistorySideBarContent;
